import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ContextService } from '@services/context';
import { LogsService } from '@services/logs';
import { ResponsiveService } from '@services/responsive';
import { ImageComponent } from '@shared/components/image';
import { InstagramDialogComponent } from '@shared/components/instagram/instagram-dialog';
import { InsideViewportDirective } from '@shared/directives';
import { of, Subject, takeUntil } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { InstagramService } from './services';

@Component({
  selector: 'app-instagram',
  providers: [InstagramService],
  templateUrl: './instagram.component.html',
  styleUrl: './instagram.component.scss',
  imports: [InsideViewportDirective, InstagramDialogComponent, ImageComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstagramComponent implements OnDestroy {
  readonly carouselContent = viewChild.required<ElementRef<HTMLElement>>('carouselContent');
  readonly carouselItem = viewChild.required<ElementRef<HTMLElement>>('carouselItem');
  readonly carouselItemContainer = viewChildren<ElementRef<HTMLElement>>('carouselItemContainer');
  cardSize = signal<number>(null!);
  widthChange = signal<number>(null!);
  isTouchEndActive = signal(false);
  readonly #instagramService = inject(InstagramService);
  readonly #logsService = inject(LogsService);
  readonly #responsiveService = inject(ResponsiveService);
  readonly #contextService = inject(ContextService);
  readonly #document = inject(DOCUMENT);
  readonly #window = this.#document.defaultView as Window;
  #destroy$ = new Subject<boolean>();
  #currentPosition = signal(0);
  #autoScrollInterval = signal<number>(null!);
  #itemsPosition = signal<number[]>([]);
  #itemsNb = signal(0);
  readonly flux = toSignal(
    this.#instagramService.getInstagramWithCdnLinks().pipe(
      tap((items) => {
        if (this.#contextService.isBrowser()) {
          this.#responsiveService
            .getWidth$()
            .pipe(
              takeUntil(this.#destroy$),
              tap((width: number) => {
                this.#itemsNb.set(items.items.length);
                this.#buildItemPosition(this.#itemsNb());
                this.#currentPosition.set(0);
                this.carouselContent()?.nativeElement.scrollTo({ left: this.#currentPosition(), behavior: 'smooth' });
                this.cardSize.set(this.carouselItem().nativeElement.clientWidth);
                this.widthChange.set(width);
              })
            )
            .subscribe();
        } else {
          this.#itemsNb.set(items.items.length);
        }
      }),
      catchError((err: unknown) => {
        this.#logsService.error('InstagramComponent', 'Instagram feed fetch failed', err);
        return of(undefined);
      })
    )
  );

  pause(): void {
    this.#window.clearInterval(this.#autoScrollInterval());
  }

  start(): void {
    this.#autoScroll();
  }

  onTouchEnd(): void {
    this.start();
    this.isTouchEndActive.set(true);
  }

  ngOnDestroy(): void {
    this.#destroy$.next(true);
    this.#destroy$.complete();
  }

  onScrollEnd(): void {
    if (this.isTouchEndActive()) {
      this.isTouchEndActive.set(false);
      const scrollLeft = this.carouselContent().nativeElement.scrollLeft;
      const positions = this.#itemsPosition();
      if (positions.length === 0) {
        return;
      }
      let closestPosition = positions[0]!;
      for (let i = 1; i < positions.length; i++) {
        const currentPosition = positions[i]!;
        if (Math.abs(currentPosition - scrollLeft) < Math.abs(closestPosition - scrollLeft)) {
          closestPosition = currentPosition;
        }
      }
      this.#currentPosition.set(closestPosition);
      this.carouselContent().nativeElement.scrollTo({
        left: this.#currentPosition(),
        behavior: 'smooth',
      });
    }
  }

  handleVisibilityChange(isVisible: boolean): void {
    if (isVisible) {
      this.start();
    } else {
      this.pause();
    }
  }

  openInstagramDialog(index: number): void {
    this.#instagramService.openDialog(index);
  }

  #scrollNext(): void {
    if (this.#isLastItemInViewport()) {
      this.#currentPosition.set(0);
    } else {
      this.#currentPosition.set(
        this.#currentPosition() + this.carouselItemContainer().at(0)!.nativeElement.clientWidth
      );
    }
    this.carouselContent().nativeElement.scrollTo({ left: this.#currentPosition(), behavior: 'smooth' });
  }

  #autoScroll(): void {
    this.#autoScrollInterval.set(
      this.#window.setInterval(() => {
        this.#scrollNext();
      }, 5000)
    ); // Tous les  5 secondes
  }

  #isLastItemInViewport(): boolean {
    const rect = this.carouselItemContainer().at(-1)!.nativeElement.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (this.#window.innerHeight || this.#document.documentElement.clientHeight) &&
      rect.right <= (this.#window.innerWidth || this.#document.documentElement.clientWidth)
    );
  }

  #buildItemPosition(nbItems: number): void {
    this.#itemsPosition().length = 0;
    const size = this.carouselItemContainer().at(0)!.nativeElement.clientWidth;
    const newPositions = Array.from({ length: nbItems }, (_, i) => i * size);
    this.#itemsPosition.set(newPositions);
  }
}
